import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { FeatureFlags, SiteConfigs } from 'app/api/services/site-configs/types';

import { type ChangeConfigValue, type ChangeConfigValuePayload, getSiteConfigs } from './service';
import { type TransformedSiteConfigs, transformPresetItem } from './transform-preset-item';

// ---

export type SiteConfigState = TransformedSiteConfigs;

type GetSiteConfigAction = ReturnType<typeof getSiteConfigs.fulfilled> | PayloadAction<SiteConfigs>;

const initialState: SiteConfigState = {
  featureFlags: [],
  freshchatConfig: null,
};

// ---

function addSiteConfigData(
  state: SiteConfigState,
  action: GetSiteConfigAction,
) {
  const payload = 'data' in action.payload
    ? action.payload.data
    : action.payload;
  const configKeys = Object.keys(payload) as (keyof SiteConfigs)[];
  configKeys.forEach((name) => {
    if (name === 'betPresets') {
      state.betPresets = transformPresetItem(payload.betPresets);
    }
    else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state[name] = payload[name];
    }
  });
}

// ---

export const siteConfigsSlice = createSlice({
  name: 'siteConfigs',
  initialState,
  reducers: {
    changeConfigValue: <T extends keyof SiteConfigs>(
      state: SiteConfigState,
      action: ChangeConfigValue<T>,
    ) => {
      if (state === null) {
        return;
      }

      state[action.payload.name] = action.payload.value;
    },
    fetchSiteConfigsSuccess: addSiteConfigData,
    featureFlagsAction: (state: SiteConfigState, action: PayloadAction<FeatureFlags[]>) => {
      if (state === null) {
        return;
      }
      const featureFlags = new Set([...(state.featureFlags || []), ...action.payload]);
      state.featureFlags = Array.from(featureFlags);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteConfigs.fulfilled, addSiteConfigData);
  },
});

const { reducer, actions } = siteConfigsSlice;

type ChangeConfigValueAction = <T extends keyof SiteConfigs>(
  payload: ChangeConfigValuePayload<T>,
) => ChangeConfigValue<T>;

export const changeConfigValue = actions.changeConfigValue as ChangeConfigValueAction;

export const {
  fetchSiteConfigsSuccess,
  featureFlagsAction,
} = actions;

export default reducer;
